import { GameState } from './types';
import { Player } from './Player';
import { EnemyManager } from './EnemyManager';
import { BulletManager } from './BulletManager';
import { InputManager } from './InputManager';
import { ExplosionManager } from './ExplosionManager';
import { ScorePopupManager } from './ScorePopupManager';
import { TrailManager } from './TrailEffect';
import { ScreenShake } from './ScreenShake';
import { ComboSystem } from './ComboSystem';
import { PowerUpManager, PowerUpType } from './PowerUpSystem';
import { BombExplosion, ShieldEffect } from './SpecialWeapons';
import { collisionCheck } from './utils';

export class Game {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private gameState: GameState;
  private player: Player;
  private enemyManager: EnemyManager;
  private bulletManager: BulletManager;
  private inputManager: InputManager;
  private explosionManager: ExplosionManager;
  private scorePopupManager: ScorePopupManager;
  private trailManager: TrailManager;
  private screenShake: ScreenShake;
  private comboSystem: ComboSystem;
  private powerUpManager: PowerUpManager;
  private bombExplosions: BombExplosion[] = [];
  private shieldEffect: ShieldEffect | null = null;
  private lastTime: number = 0;
  private gameOverTimer: number = 0;
  private gameOverDelay: number = 3.0;
  private stageTransitionTimer: number = 0;
  private stageTransitionDelay: number = 3.0;
  private stageClearTimer: number = 0;
  private stageClearDelay: number = 2.0;
  private scoreElement: HTMLElement;
  private livesElement: HTMLElement;
  private debugInvincible: boolean = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    
    this.gameState = {
      score: 0,
      lives: 3,
      level: 1,
      stage: 1,
      gameOver: false,
      paused: false,
      started: false,
      stageTransition: false,
      stageClear: false,
      gameCompleted: false
    };

    this.player = new Player(canvas.width / 2 - 25, canvas.height - 80, canvas.width);
    this.enemyManager = new EnemyManager(canvas.width, canvas.height);
    this.bulletManager = new BulletManager();
    this.inputManager = new InputManager();
    this.explosionManager = new ExplosionManager();
    this.scorePopupManager = new ScorePopupManager();
    this.trailManager = new TrailManager();
    this.screenShake = new ScreenShake();
    this.comboSystem = new ComboSystem();
    this.powerUpManager = new PowerUpManager();
    
    // 初期ボムを1発付与
    this.powerUpManager.activatePowerUp(PowerUpType.BOMB);
    
    this.bulletManager.setTrailManager(this.trailManager);

    this.scoreElement = document.getElementById('score')!;
    this.livesElement = document.getElementById('lives')!;

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.inputManager.setup();
    this.setupRetryButton();
  }

  private setupRetryButton(): void {
    this.canvas.addEventListener('click', this.handleCanvasClick.bind(this));
  }

  private handleCanvasClick(event: MouseEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    if (!this.gameState.started) {
      const buttonX = this.canvas.width / 2 - 75;
      const buttonY = this.canvas.height / 2 + 20;
      const buttonWidth = 150;
      const buttonHeight = 40;

      if (x >= buttonX && x <= buttonX + buttonWidth &&
          y >= buttonY && y <= buttonY + buttonHeight) {
        this.startGame();
      }
    } else if (this.gameState.gameOver) {
      const buttonX = this.canvas.width / 2 - 75;
      const buttonY = this.canvas.height / 2 + 80;
      const buttonWidth = 150;
      const buttonHeight = 40;

      if (x >= buttonX && x <= buttonX + buttonWidth &&
          y >= buttonY && y <= buttonY + buttonHeight) {
        this.restartGame();
      }
    } else if (this.gameState.gameCompleted) {
      const buttonX = this.canvas.width / 2 - 75;
      const buttonY = this.canvas.height / 2 + 100;
      const buttonWidth = 150;
      const buttonHeight = 40;

      if (x >= buttonX && x <= buttonX + buttonWidth &&
          y >= buttonY && y <= buttonY + buttonHeight) {
        this.restartGame();
      }
    }
  }

  private startGame(): void {
    this.gameState.started = true;
    this.gameState.stageTransition = true;
    this.stageTransitionTimer = 0;
    const gameUI = document.getElementById('gameUI');
    if (gameUI) {
      gameUI.style.display = 'flex';
    }
  }

  private restartGame(): void {
    this.gameState = {
      score: 0,
      lives: 3,
      level: 1,
      stage: 1,
      gameOver: false,
      paused: false,
      started: true,
      stageTransition: true,
      stageClear: false,
      gameCompleted: false
    };

    this.player.reset(this.canvas.width / 2 - 25, this.canvas.height - 80);
    this.player.alive = true;
    this.enemyManager.clear();
    this.enemyManager.spawnWave(1);
    this.bulletManager.clear();
    this.explosionManager.clear();
    this.scorePopupManager.clear();
    this.trailManager.clear();
    this.screenShake.clear();
    this.comboSystem.clear();
    this.powerUpManager.clear();
    // リスタート時にボムを1発付与
    this.powerUpManager.activatePowerUp(PowerUpType.BOMB);
    this.bombExplosions = [];
    this.shieldEffect = null;
    this.gameOverTimer = 0;
    this.stageTransitionTimer = 0;
    this.stageClearTimer = 0;
    
    const gameUI = document.getElementById('gameUI');
    if (gameUI) {
      gameUI.style.display = 'flex';
    }
  }

  public start(): void {
    this.enemyManager.clear();
    this.gameLoop(0);
  }

  private gameLoop = (currentTime: number): void => {
    const deltaTime = (currentTime - this.lastTime) / 1000;
    this.lastTime = currentTime;

    // ステージクリア状態の管理（常に実行）
    if (this.gameState.stageClear) {
      this.stageClearTimer += deltaTime;
      if (this.stageClearTimer >= this.stageClearDelay) {
        this.gameState.stageClear = false;
        if (this.gameState.stage >= 4) {
          // ステージ4クリア後はゲーム完了
          console.log("All stages completed! Moving to game completion.");
          this.gameState.gameCompleted = true;
        } else {
          // 次のステージに進む
          console.log(`Moving from stage ${this.gameState.stage} to stage ${this.gameState.stage + 1}`);
          this.gameState.stage++;
          this.gameState.stageTransition = true;
          this.stageTransitionTimer = 0;
        }
      }
    }

    if (!this.gameState.paused && this.gameState.started && !this.gameState.stageTransition && !this.gameState.gameCompleted) {
      this.update(deltaTime);
    } else if (this.gameState.stageTransition) {
      this.stageTransitionTimer += deltaTime;
      if (this.stageTransitionTimer >= this.stageTransitionDelay) {
        this.gameState.stageTransition = false;
        this.enemyManager.spawnWave(this.gameState.level, this.gameState.stage);
      }
    } else if (this.explosionManager.isPlayerDeathExplosionActive()) {
      this.explosionManager.update(deltaTime);
      this.screenShake.update(deltaTime);
      this.gameOverTimer += deltaTime;
      if (this.gameOverTimer >= this.gameOverDelay) {
        this.gameState.gameOver = true;
      }
    } else if (this.gameState.lives <= 0) {
      this.gameState.gameOver = true;
    }
    
    this.render();
    requestAnimationFrame(this.gameLoop);
  };

  private update(deltaTime: number): void {
    this.explosionManager.update(deltaTime);
    this.screenShake.update(deltaTime);
    this.scorePopupManager.update(deltaTime);
    this.trailManager.update();
    this.powerUpManager.update(deltaTime, this.gameState.score);
    
    this.bombExplosions.forEach(explosion => explosion.update(deltaTime));
    this.bombExplosions = this.bombExplosions.filter(explosion => explosion.alive);
    
    if (this.shieldEffect && this.powerUpManager.isEffectActive(PowerUpType.SHIELD)) {
      this.shieldEffect.update(deltaTime, this.player.position.x + this.player.width / 2, this.player.position.y + this.player.height / 2);
    } else {
      this.shieldEffect = null;
    }
    
    if (this.explosionManager.isPlayerDeathExplosionActive()) {
      this.gameOverTimer += deltaTime;
      if (this.gameOverTimer >= this.gameOverDelay) {
        this.gameState.gameOver = true;
      }
      return;
    }
    
    if (this.gameState.lives <= 0 && !this.explosionManager.isPlayerDeathExplosionActive()) {
      this.gameState.gameOver = true;
      return;
    }

    if (this.gameState.gameOver) {
      return;
    }

    this.handleInput();
    this.player.update(deltaTime);
    
    const pickedUpItem = this.powerUpManager.checkCollision(
      this.player.position.x, 
      this.player.position.y, 
      this.player.width, 
      this.player.height
    );
    
    if (pickedUpItem) {
      this.activatePowerUp(pickedUpItem);
    }
    this.enemyManager.update(deltaTime, this.player.position.x + this.player.width / 2, this.player.position.y + this.player.height / 2, this.gameState.stage);
    this.bulletManager.update(deltaTime);
    this.comboSystem.update(deltaTime);

    this.checkCollisions();
    this.updateUI();

    if (this.gameState.lives <= 0 && !this.explosionManager.isPlayerDeathExplosionActive()) {
      this.gameState.gameOver = true;
      return;
    }

    if (this.enemyManager.allEnemiesDestroyed() && !this.gameState.stageClear && !this.gameState.stageTransition && !this.gameState.gameCompleted) {
      this.nextStage();
    }

  }

  private handleInput(): void {
    const input = this.inputManager.getInput();
    
    if (input.left) {
      this.player.moveLeft();
    }
    if (input.right) {
      this.player.moveRight();
    }
    if (input.shoot) {
      const rapidFire = this.powerUpManager.isEffectActive(PowerUpType.RAPID_FIRE);
      const penetrating = this.powerUpManager.isEffectActive(PowerUpType.PENETRATING);
      
      const bullets = this.player.shoot(rapidFire, penetrating);
      bullets.forEach(bullet => this.bulletManager.addPlayerBullet(bullet));
    }
    
    if (input.bomb && this.powerUpManager.getBombCount() > 0) {
      this.useBomb();
    }
    
    if (input.toggleInvincible) {
      this.debugInvincible = !this.debugInvincible;
      console.log(`Debug invincible mode: ${this.debugInvincible ? 'ON' : 'OFF'}`);
    }
    
    // デバッグ用ステージジャンプ
    if (input.jumpToStage1) {
      this.jumpToStage(1);
    } else if (input.jumpToStage2) {
      this.jumpToStage(2);
    } else if (input.jumpToStage3) {
      this.jumpToStage(3);
    } else if (input.jumpToStage4) {
      this.jumpToStage(4);
    }
  }

  private checkCollisions(): void {
    if (this.gameState.lives <= 0 || this.explosionManager.isPlayerDeathExplosionActive() || !this.player.alive) {
      return;
    }
    
    const playerBullets = this.bulletManager.getPlayerBullets();
    const enemyBullets = this.bulletManager.getEnemyBullets();
    const enemies = this.enemyManager.getEnemies();

    playerBullets.forEach(bullet => {
      enemies.forEach(enemy => {
        if (collisionCheck(bullet, enemy)) {
          let shouldRemoveBullet = true;
          
          if ('onHit' in bullet) {
            shouldRemoveBullet = (bullet as any).onHit();
          }
          
          if (shouldRemoveBullet) {
            bullet.alive = false;
          }
          
          enemy.health--;
          if (enemy.health <= 0) {
            enemy.alive = false;
            this.comboSystem.addKill();
            const finalScore = enemy.score * this.comboSystem.getScoreMultiplier();
            this.gameState.score += finalScore;
            this.scorePopupManager.addScorePopup(enemy.position.x + enemy.width / 2, enemy.position.y, finalScore);
            const enemyType = enemy.score >= 400 ? 'large' : enemy.score >= 150 ? 'medium' : 'small';
            this.explosionManager.addEnemyExplosion(enemy.position.x + enemy.width / 2, enemy.position.y + enemy.height / 2, enemyType);
            if (enemyType === 'large') {
              this.screenShake.shake(10, 0.3);
            }
          }
        }
      });
    });

    enemyBullets.forEach(bullet => {
      if (collisionCheck(bullet, this.player)) {
        if (this.powerUpManager.isEffectActive(PowerUpType.SHIELD) || this.debugInvincible) {
          bullet.alive = false;
          return;
        }
        
        bullet.alive = false;
        this.player.takeDamage();
        this.explosionManager.addExplosion(this.player.position.x + this.player.width / 2, this.player.position.y + this.player.height / 2);
        this.screenShake.shake(5, 0.2);
        if (this.player.health <= 0) {
          this.gameState.lives--;
          if (this.gameState.lives <= 0) {
            this.explosionManager.addPlayerDeathExplosion(this.player.position.x + this.player.width / 2, this.player.position.y + this.player.height / 2);
            this.screenShake.shake(15, 0.5);
            this.gameOverTimer = 0;
            this.player.alive = false;
          } else {
            this.player.reset(this.canvas.width / 2 - 25, this.canvas.height - 80);
          }
        }
      }
    });

    enemies.forEach(enemy => {
      if (collisionCheck(enemy, this.player)) {
        if (this.powerUpManager.isEffectActive(PowerUpType.SHIELD) || this.debugInvincible) {
          enemy.alive = false;
          return;
        }
        
        enemy.alive = false;
        this.player.takeDamage();
        this.explosionManager.addExplosion(this.player.position.x + this.player.width / 2, this.player.position.y + this.player.height / 2);
        this.screenShake.shake(5, 0.2);
        if (this.player.health <= 0) {
          this.gameState.lives--;
          if (this.gameState.lives <= 0) {
            this.explosionManager.addPlayerDeathExplosion(this.player.position.x + this.player.width / 2, this.player.position.y + this.player.height / 2);
            this.screenShake.shake(15, 0.5);
            this.gameOverTimer = 0;
            this.player.alive = false;
          } else {
            this.player.reset(this.canvas.width / 2 - 25, this.canvas.height - 80);
          }
        }
      }
    });

    const enemyBullets2 = this.enemyManager.getBullets();
    enemyBullets2.forEach(bullet => {
      this.bulletManager.addEnemyBullet(bullet);
    });
    
    this.bombExplosions.forEach(explosion => {
      enemies.forEach(enemy => {
        if (explosion.isInRange(enemy.position.x + enemy.width / 2, enemy.position.y + enemy.height / 2)) {
          enemy.alive = false;
          this.comboSystem.addKill();
          const finalScore = enemy.score * this.comboSystem.getScoreMultiplier();
          this.gameState.score += finalScore;
          this.scorePopupManager.addScorePopup(enemy.position.x + enemy.width / 2, enemy.position.y, finalScore);
        }
      });
    });
  }

  private activatePowerUp(type: PowerUpType): void {
    this.powerUpManager.activatePowerUp(type);
    
    if (type === PowerUpType.SHIELD) {
      this.shieldEffect = new ShieldEffect(
        this.player.position.x + this.player.width / 2,
        this.player.position.y + this.player.height / 2
      );
    }
  }

  private useBomb(): void {
    if (this.powerUpManager.useBomb()) {
      // 爆発エフェクトを追加
      this.bombExplosions.push(new BombExplosion(
        this.player.position.x + this.player.width / 2, // プレイヤーのX座標中央
        this.canvas.height / 2 // Y座標は画面中央のまま
      ));
      
      // 敵の弾をすべて消去
      this.bulletManager.clearEnemyBullets();
      
      // 画面揺れ
      this.screenShake.shake(20, 0.8);
    }
  }

  private nextStage(): void {
    // ステージクリア時に自分の弾をクリア
    this.bulletManager.clearPlayerBullets();
    
    console.log(`Current stage: ${this.gameState.stage}, checking completion...`);
    
    // ステージクリア表示を開始
    this.gameState.stageClear = true;
    this.stageClearTimer = 0;
  }

  private updateUI(): void {
    this.scoreElement.textContent = this.gameState.score.toString();
    this.livesElement.textContent = this.gameState.lives.toString();
  }

  private render(): void {
    this.ctx.save();
    
    const shake = this.screenShake.getOffset();
    this.ctx.translate(shake.x, shake.y);
    
    this.ctx.fillStyle = '#000';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    if (!this.gameState.started) {
      this.renderStartScreen();
    } else if (this.gameState.gameCompleted) {
      this.renderGameCompleted();
    } else if (this.gameState.stageTransition) {
      this.renderStageTransition();
    } else {
      this.trailManager.render(this.ctx);
      
      if (!this.explosionManager.isPlayerDeathExplosionActive()) {
        this.renderPlayer();
        this.comboSystem.render(this.ctx, 20, 100);
        this.enemyManager.render(this.ctx);
        this.bulletManager.render(this.ctx);
      }
      
      this.renderDebugInfo();
      
      // ステージクリア表示をオーバーレイとして描画
      if (this.gameState.stageClear) {
        this.renderStageClearOverlay();
      }
      
      this.explosionManager.render(this.ctx);
      this.scorePopupManager.render(this.ctx);
      this.powerUpManager.render(this.ctx);
      this.bombExplosions.forEach(explosion => explosion.render(this.ctx));
      
      if (this.shieldEffect) {
        this.shieldEffect.render(this.ctx);
      }

      if (this.gameState.gameOver) {
        this.renderGameOver();
      }
    }
    
    if (this.explosionManager.isPlayerDeathExplosionActive()) {
      this.explosionManager.render(this.ctx);
    }
    
    this.ctx.restore();
  }

  private renderGameOver(): void {
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.ctx.fillStyle = '#fff';
    this.ctx.font = '48px Arial';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('GAME OVER', this.canvas.width / 2, this.canvas.height / 2);
    
    this.ctx.font = '24px Arial';
    this.ctx.fillText(`Final Score: ${this.gameState.score}`, this.canvas.width / 2, this.canvas.height / 2 + 50);

    const buttonX = this.canvas.width / 2 - 75;
    const buttonY = this.canvas.height / 2 + 80;
    const buttonWidth = 150;
    const buttonHeight = 40;

    this.ctx.fillStyle = '#ff6600';
    this.ctx.fillRect(buttonX, buttonY, buttonWidth, buttonHeight);
    
    this.ctx.fillStyle = '#fff';
    this.ctx.font = '20px Arial';
    this.ctx.fillText('RETRY', this.canvas.width / 2, buttonY + 26);
  }

  private renderStartScreen(): void {
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.ctx.fillStyle = '#fff';
    this.ctx.font = '48px Arial';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('SPACE SHOOTER', this.canvas.width / 2, this.canvas.height / 2 - 80);
    
    this.ctx.font = '20px Arial';
    this.ctx.fillStyle = '#aaa';
    this.ctx.fillText('Invader + Galaxian + Bullet Hell', this.canvas.width / 2, this.canvas.height / 2 - 40);

    const buttonX = this.canvas.width / 2 - 75;
    const buttonY = this.canvas.height / 2 + 20;
    const buttonWidth = 150;
    const buttonHeight = 40;

    this.ctx.fillStyle = '#00ff00';
    this.ctx.fillRect(buttonX, buttonY, buttonWidth, buttonHeight);
    
    this.ctx.fillStyle = '#000';
    this.ctx.font = '20px Arial';
    this.ctx.fillText('START GAME', this.canvas.width / 2, buttonY + 26);

    this.ctx.fillStyle = '#888';
    this.ctx.font = '16px Arial';
    this.ctx.fillText('Controls: Arrow Keys / A,D - Move | Space / Z - Shoot', this.canvas.width / 2, this.canvas.height - 50);
  }

  private renderStageTransition(): void {
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.ctx.fillStyle = '#fff';
    this.ctx.font = '64px Arial';
    this.ctx.textAlign = 'center';
    this.ctx.fillText(`STAGE ${this.gameState.stage}`, this.canvas.width / 2, this.canvas.height / 2);
    
    // ステージ別の説明
    this.ctx.font = '24px Arial';
    this.ctx.fillStyle = '#aaa';
    let description = '';
    switch (this.gameState.stage) {
      case 1:
        description = 'Enemies: Few - Prepare for battle!';
        break;
      case 2:
        description = 'Enemies: Medium - Getting serious!';
        break;
      case 3:
        description = 'Enemies: Many - Final assault!';
        break;
      case 4:
        description = 'Enemies: Maximum - Ultimate challenge!';
        break;
    }
    this.ctx.fillText(description, this.canvas.width / 2, this.canvas.height / 2 + 50);
    
    // カウントダウン表示
    const remaining = Math.ceil(this.stageTransitionDelay - this.stageTransitionTimer);
    this.ctx.font = '32px Arial';
    this.ctx.fillStyle = '#ffff00';
    this.ctx.fillText(`Starting in ${remaining}...`, this.canvas.width / 2, this.canvas.height / 2 + 100);
  }

  private renderGameCompleted(): void {
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.ctx.fillStyle = '#ffff00';
    this.ctx.font = '48px Arial';
    this.ctx.textAlign = 'center';
    this.ctx.fillText('CONGRATULATIONS!', this.canvas.width / 2, this.canvas.height / 2 - 50);
    
    this.ctx.fillStyle = '#fff';
    this.ctx.font = '32px Arial';
    this.ctx.fillText('All Stages Completed!', this.canvas.width / 2, this.canvas.height / 2);
    
    this.ctx.font = '24px Arial';
    this.ctx.fillText(`Final Score: ${this.gameState.score}`, this.canvas.width / 2, this.canvas.height / 2 + 50);
    
    // Retryボタン
    const buttonX = this.canvas.width / 2 - 75;
    const buttonY = this.canvas.height / 2 + 100;
    const buttonWidth = 150;
    const buttonHeight = 40;

    this.ctx.fillStyle = '#00aa00';
    this.ctx.fillRect(buttonX, buttonY, buttonWidth, buttonHeight);
    
    this.ctx.fillStyle = '#fff';
    this.ctx.font = '20px Arial';
    this.ctx.fillText('RETRY', this.canvas.width / 2, buttonY + 26);
    
    // 星の装飾
    this.ctx.fillStyle = '#ffff00';
    this.ctx.font = '40px Arial';
    this.ctx.fillText('★', this.canvas.width / 2 - 100, this.canvas.height / 2 - 50);
    this.ctx.fillText('★', this.canvas.width / 2 + 100, this.canvas.height / 2 - 50);
    this.ctx.fillText('★', this.canvas.width / 2, this.canvas.height / 2 - 120);
  }

  private renderPlayer(): void {
    if (this.debugInvincible) {
      // 無敵時は点滅効果
      const blinkSpeed = 5;
      const opacity = Math.sin(Date.now() * blinkSpeed / 1000) > 0 ? 1 : 0.3;
      this.ctx.globalAlpha = opacity;
    }
    
    this.player.render(this.ctx);
    this.ctx.globalAlpha = 1;
  }

  private renderDebugInfo(): void {
    if (this.debugInvincible) {
      this.ctx.fillStyle = '#ffff00';
      this.ctx.font = '16px Arial';
      this.ctx.textAlign = 'left';
      this.ctx.fillText('DEBUG: INVINCIBLE MODE ON', 10, 30);
      this.ctx.fillText('Press I to toggle invincible', 10, 50);
      this.ctx.fillText('Press 1-4 to jump to stage', 10, 70);
    }
  }

  private renderStageClearOverlay(): void {
    // 軽い背景オーバーレイ（任意）
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Stage Clearテキスト
    this.ctx.fillStyle = '#00ff00';
    this.ctx.font = '48px Arial';
    this.ctx.textAlign = 'center';
    this.ctx.strokeStyle = '#000000';
    this.ctx.lineWidth = 3;
    this.ctx.strokeText('STAGE CLEAR', this.canvas.width / 2, 150);
    this.ctx.fillText('STAGE CLEAR', this.canvas.width / 2, 150);
    
    // ステージ番号
    this.ctx.fillStyle = '#ffff00';
    this.ctx.font = '24px Arial';
    this.ctx.strokeStyle = '#000000';
    this.ctx.lineWidth = 2;
    this.ctx.strokeText(`Stage ${this.gameState.stage} Completed!`, this.canvas.width / 2, 185);
    this.ctx.fillText(`Stage ${this.gameState.stage} Completed!`, this.canvas.width / 2, 185);
  }

  private jumpToStage(stage: number): void {
    console.log(`Debug: Jumping to stage ${stage}`);
    
    // ゲーム状態をリセット
    this.gameState.stage = stage;
    this.gameState.lives = 3;
    this.gameState.gameOver = false;
    this.gameState.stageTransition = true;
    this.gameState.stageClear = false;
    this.gameState.gameCompleted = false;
    this.gameState.started = true;
    
    // タイマーリセット
    this.stageTransitionTimer = 0;
    this.stageClearTimer = 0;
    this.gameOverTimer = 0;
    
    // プレイヤーリセット
    this.player.reset(this.canvas.width / 2 - 25, this.canvas.height - 80);
    this.player.alive = true;
    
    // エンティティクリア
    this.enemyManager.clear();
    this.bulletManager.clear();
    this.explosionManager.clear();
    this.scorePopupManager.clear();
    this.trailManager.clear();
    this.screenShake.clear();
    this.comboSystem.clear();
    this.powerUpManager.clear();
    // ステージジャンプ時にボムを1発付与
    this.powerUpManager.activatePowerUp(PowerUpType.BOMB);
    this.bombExplosions = [];
    this.shieldEffect = null;
    
    // UIを表示
    const gameUI = document.getElementById('gameUI');
    if (gameUI) {
      gameUI.style.display = 'flex';
    }
    
    console.log(`Jumped to stage ${stage}, starting stage transition`);
  }
}